import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Gift, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface Profile {
  username: string;
  full_name: string;
  avatar_url: string;
}

const Profile = () => {
  const { user } = useAuth();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [publicRegistry, setPublicRegistry] = useState(false);
  const [profile, setProfile] = useState<Profile>({
    username: '',
    full_name: '',
    avatar_url: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const sparklesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadProfile();

    const createSparkle = () => {
      if (!sparklesRef.current) return;
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      
      sparklesRef.current.appendChild(sparkle);
      
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);

    for (let i = 0; i < 10; i++) {
      setTimeout(createSparkle, i * 100);
    }

    return () => clearInterval(interval);
  }, [user]);

  async function loadProfile() {
    try {
      if (!user) return;
      setLoading(true);
      setError('');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('username, full_name, avatar_url')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      if (data) {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (!user) return;

      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          username: profile.username,
          full_name: profile.full_name,
          avatar_url: profile.avatar_url,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
      setSuccess('Profile updated successfully!');
      await loadProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div ref={sparklesRef} className="sparkles-container" />

      <div className="card-pastel overflow-hidden">
        <div className="bg-white/60 p-6">
          <div className="flex items-center space-x-4">
            <div className="w-24 h-24 bg-pastel-purple-500 text-white rounded-full flex items-center justify-center">
              <User className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {profile.full_name || user?.email?.split('@')[0] || 'User'}
              </h1>
              <p className="text-gray-600">@{profile.username || 'username'}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-100/60 border border-red-200 text-red-700 p-3 rounded-md">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-100/60 border border-green-200 text-green-700 p-3 rounded-md">
              {success}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800">Profile Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={profile.full_name}
                    onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                    className="input-pastel"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    value={profile.username}
                    onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                    className="input-pastel"
                    placeholder="Choose a username"
                  />
                </div>
                <div className="flex items-center space-x-3 text-gray-600">
                  <Mail className="w-5 h-5" />
                  <span>{user?.email}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800">Registry Statistics</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/60 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 text-pastel-purple-600 mb-2">
                    <Gift className="w-5 h-5" />
                    <span className="font-medium text-gray-800">Active Registries</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">0</p>
                </div>
                <div className="bg-white/60 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 text-pastel-purple-600 mb-2">
                    <Bell className="w-5 h-5" />
                    <span className="font-medium text-gray-800">Notifications</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">0</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/40 pt-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Preferences</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Bell className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-800">Email Notifications</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pastel-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pastel-purple-500"></div>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Gift className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-800">Public Registry</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={publicRegistry}
                    onChange={(e) => setPublicRegistry(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pastel-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pastel-purple-500"></div>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button 
              onClick={loadProfile}
              className="px-6 py-2 bg-white/60 text-gray-800 border border-white/40 rounded-md hover:bg-white/80 transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              disabled={saving}
              className="btn-pastel disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;